// severidade.js — #67 (Felipe N2.1): rótulos/cores de severidade por tenant.
//
// A IA SEMPRE emite os 4 níveis canônicos (critica/alta/media/baixa) e toda a
// lógica (filtro "críticas", sort, score) opera neles. Este util só traduz a
// EXIBIÇÃO: o tenant pode renomear o rótulo (ex: A/B/C/D, 1-2-3-4) e dar cor a
// cada nível, configurado em clients.severidade_labels e exposto no /auth/me
// como user.client_severidade_labels — igual ao timezone (formatDateTime.js).
//
// Sem config (null) → cai nos rótulos/cores default (idêntico a antes do #67).

import { cssSlug } from './cssSlug';

// Rótulos default (espelham SEVERIDADE_ROTULOS_DEFAULT do backend).
const ROTULOS_DEFAULT = {
  critica: 'Crítica',
  alta: 'Alta',
  media: 'Média',
  baixa: 'Baixa',
};

/**
 * Resolve o mapa de severidade do tenant a partir de várias fontes.
 * Ordem: explícito > user.client_severidade_labels > localStorage > {}.
 * @returns {object} mapa {nivel: {rotulo, cor}} (pode ser parcial ou vazio)
 */
function resolveLabels(source) {
  if (source && typeof source === 'object') {
    // objeto já é o mapa de labels?
    if (source.critica || source.alta || source.media || source.baixa) return source;
    // objeto user com a chave?
    if (source.client_severidade_labels) return source.client_severidade_labels || {};
  }
  try {
    const cached = localStorage.getItem('user');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed?.client_severidade_labels) return parsed.client_severidade_labels;
    }
  } catch {
    // localStorage indisponível/corrompido — segue pro default
  }
  return {};
}

/** Normaliza um valor de severidade (com acentos/maiúsc.) pro slug canônico. */
function nivelCanonico(value) {
  const s = cssSlug(value);
  return ['critica', 'alta', 'media', 'baixa'].includes(s) ? s : 'media';
}

/**
 * Rótulo de exibição da severidade (custom do tenant ou default).
 * @param {string} value valor canônico vindo da NC ("Crítica", "alta"...)
 * @param {string|object|null} source mapa de labels, objeto user, ou null
 */
export function getSeveridadeLabel(value, source = null) {
  const nivel = nivelCanonico(value);
  const labels = resolveLabels(source);
  const custom = labels[nivel];
  const rotulo = custom && typeof custom === 'object' ? (custom.rotulo || '').trim() : '';
  return rotulo || ROTULOS_DEFAULT[nivel];
}

/**
 * Cor custom da severidade (#rrggbb) ou null quando não configurada
 * (null → o chamador mantém a cor default do tema/CSS).
 */
export function getSeveridadeCor(value, source = null) {
  const nivel = nivelCanonico(value);
  const labels = resolveLabels(source);
  const custom = labels[nivel];
  const cor = custom && typeof custom === 'object' ? custom.cor : null;
  return /^#[0-9a-fA-F]{6}$/.test(cor || '') ? cor : null;
}

/**
 * Cor de texto legível (#fff ou escuro) pra um fundo hex, via luminância
 * relativa (WCAG). Usado onde a cor custom vira BACKGROUND do badge.
 */
export function corTextoContraste(hexBg) {
  if (!/^#[0-9a-fA-F]{6}$/.test(hexBg || '')) return '#ffffff';
  const r = parseInt(hexBg.slice(1, 3), 16) / 255;
  const g = parseInt(hexBg.slice(3, 5), 16) / 255;
  const b = parseInt(hexBg.slice(5, 7), 16) / 255;
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const lum = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return lum > 0.5 ? '#1a1a1a' : '#ffffff';
}

export { ROTULOS_DEFAULT };
